import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { erApiFeil } from '../api/feil.ts';
import { useAvsluttProgram, useDeltakere, useInvitasjoner, useInviter, useRapport, type Invitation, type Participant, type Program, type ProgramReportCreate } from '../api/usePrograms.ts';
import { useSaker } from '../api/useSubmissions.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { Sporingslinje } from '../innboks/Sporingslinje.tsx';
import { Dialog } from '../skall/Dialog.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { ProgramPille } from '../status/Pille.tsx';
import { Om } from '../skall/Om.tsx';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { kortSubject } from './periode.ts';
import { HypoteseRad, SmuleHypotese } from './HypoteseRad.tsx';
import { Avslutt, FrossenRapport } from './Rapport.tsx';
import { Tallrekke } from './Tallrekke.tsx';
import { helten, sporLedd, tallrekke } from './tilstand.ts';

/** Invitasjonens status (kontraktens `InvitationStatus`) som ikon + ord — aldri farge alene (DD-08). */
const INVITASJONSSTATUS: Record<string, { ikon: string; navn: string; tone?: string }> = {
  sendt: { ikon: 'mail-outline', navn: 'Sendt' },
  akseptert: { ikon: 'checkmark-circle-outline', navn: 'Akseptert', tone: 'panel-prg-ok' },
  avslaatt: { ikon: 'close-circle-outline', navn: 'Avslått' },
  utloept: { ikon: 'time-outline', navn: 'Utløpt', tone: 'panel-prg-venter' },
};

/** Deltakerordene (DD-60, presisert P5). Fire ord, ett per tilstand — «Med» = flagget bekreftet. */
function deltakerord(d: Participant): { ikon: string; navn: string; tone?: string } {
  if (d.status === 'trukket') return { ikon: 'exit-outline', navn: `Trukket${d.withdrawalConfirmed ? '' : ' · flagg ikke bekreftet av'}`, tone: 'panel-prg-venter' };
  if (d.flagConfirmedAt) return { ikon: 'checkmark-circle-outline', navn: 'Med' };
  return { ikon: 'ellipse-outline', navn: 'Ikke slått på', tone: 'panel-prg-venter' };
}

/**
 * Så mange deltakere står i tabellen før «Vis alle N» — resten er ett trykk unna.
 * Tabellen er skjermens tyngste tekstmengde; fem rader er nok til å se mønsteret uten at
 * siden blir en liste (R2.3s ordtak).
 */
const FØRSTE_DELTAKERE = 5;

/**
 * Programsiden (R2.3, program-konsept v0.1 skjerm 1–3 og 5).
 *
 * Hypotesen er overskriftens setning og «Vi har rett hvis … er …» står rett under, på ALLE
 * program (DD-17). Datoen er skjermens ene helt; på et avsluttet program er det rapportens
 * konklusjon, og datoen står mindre (DD-67). Sporingslinjen er innboksens komponent, med
 * datoene kontrakten har.
 *
 * Tellingene er TELLINGER — aldri en rangering av testere, ingen poeng, ingen toppliste
 * (DD-38): tabellens kolonner kan ikke sorteres, og det finnes ingen sum. Reglene bak
 * tallene står i «Om tallene», ikke på flaten (F9, F15).
 *
 * «Inviter» finnes BARE i Åpen (DD-12): i Aktiv vises ingen deaktivert boks — den er borte.
 */
export function Programside({
  program,
  produktnavn,
  kanSkrive,
  opptatt,
  onRediger,
  onAvbryt,
  onTilbake,
}: {
  program: Program;
  /** Produktets navn til smulen. Flaggnavnet er leietakerens og styrer ingenting. */
  produktnavn?: string;
  kanSkrive: boolean;
  opptatt: boolean;
  onRediger: () => void;
  onAvbryt: (grunn: string) => void;
  /** Under breakpointen (DD-51) tar siden lista sin plass; «Tilbake til lista» står øverst. */
  onTilbake?: () => void;
}) {
  const invitasjoner = useInvitasjoner(program.id);
  const deltakere = useDeltakere(program.id);
  const inviter = useInviter(program.id);
  const rapport = useRapport(program.id);
  const avslutt = useAvsluttProgram(program.id);
  const smal = useSmal();

  // Funnene: programmets innspill havner i SAMME innboks som alt annet, merket med
  // programmet — ett triage-sted. Her telles de bare.
  const funn = useSaker({ programId: program.id });
  const saker = useMemo(() => funn.data?.pages.flatMap((p) => p.items) ?? [], [funn.data]);
  const kjentFeilTreff = saker.filter((s) => s.status === 'kjent_feil').length;

  const [pseudonymer, setPseudonymer] = useState('');
  const [samtykke, setSamtykke] = useState(false);
  const [tokens, setTokens] = useState<Invitation[] | null>(null);
  const [feil, setFeil] = useState<string | null>(null);
  const [avbryter, setAvbryter] = useState(false);
  const [avslutter, setAvslutter] = useState(false);
  const [mer, setMer] = useState(false);
  const [alleDeltakere, setAlleDeltakere] = useState(false);
  const [grunn, setGrunn] = useState('');

  const subjects = pseudonymer
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const inviterGrunn = !kanSkrive
    ? 'Rollen leser kan ikke invitere'
    : subjects.length === 0
      ? 'Lim inn minst ett pseudonym'
      : !samtykke
        ? 'Bekreft samtykket for kanalen dere sender i — det er ikke en stille forutsetning'
        : inviter.isPending
          ? 'Oppretter …'
          : null;

  const send = () => {
    if (inviterGrunn) return;
    setFeil(null);
    inviter
      .mutateAsync(subjects)
      .then((res) => {
        setTokens(res.invitations);
        setPseudonymer('');
        setSamtykke(false);
      })
      .catch((e: unknown) => setFeil(erApiFeil(e) ? e.brukertekst : 'Kunne ikke opprette invitasjonene. Prøv igjen.'));
  };

  const rader = invitasjoner.data ?? [];
  const alle = deltakere.data ?? [];
  const viste = alleDeltakere ? alle : alle.slice(0, FØRSTE_DELTAKERE);
  const nå = new Date();
  const h = helten(program, rapport.data, nå);
  const erRapport = program.status === 'avsluttet' && rapport.data !== undefined;
  const tall = tallrekke(program, rader);
  const kanAvbryte = program.status !== 'avsluttet' && program.status !== 'avbrutt';
  const primærValg = program.status === 'aktiv' || program.status === 'avsluttes';
  const skriveGrunn = kanSkrive ? null : 'Rollen leser kan ikke endre programmer';

  return (
    <section className="panel-prg-side" aria-labelledby="prg-h" data-testid="prg-side">
      <div className="panel-prg-kropp">
        <div className="panel-prg-smule">
          <span>
            {produktnavn ? (
              <>
                {produktnavn} {program.flagRef ? <span aria-hidden="true">·</span> : null}{' '}
              </>
            ) : null}
            {program.flagRef ? (
              <>
                flagg <b className="panel-prg-flagg">{program.flagRef}</b>
              </>
            ) : null}
            {program.fromHypothesis ? (
              <>
                {produktnavn || program.flagRef ? <span aria-hidden="true"> · </span> : null}
                <SmuleHypotese hyp={program.fromHypothesis} />
              </>
            ) : null}
          </span>
          <span className="panel-prg-smule-h">
            {onTilbake ? (
              <button type="button" className="panel-knapp-lenke panel-tilbake" onClick={onTilbake}>
                <Ikon navn="arrow-back-outline" /> Tilbake til lista
              </button>
            ) : null}
            {/* På 400 px ligger «Avbryt programmet» bak «⋯» — den skal ikke være ett trykk unna. */}
            {smal && kanAvbryte ? (
              <button type="button" className="panel-knapp-liten" aria-expanded={mer} aria-label="Mer" onClick={() => setMer((x) => !x)} data-testid="prg-mer">
                <Ikon navn="ellipsis-horizontal-outline" />
              </button>
            ) : null}
          </span>
        </div>

        <div className="panel-prg-hode">
          <div className="panel-prg-hode-tekst">
            <h2 id="prg-h" className="panel-prg-tittel">
              {program.name} <ProgramPille status={program.status} liten />
            </h2>
            {program.hypothesis ? (
              <p className="panel-prg-hypotese" data-testid="prg-hypotese">
                {program.hypothesis}
              </p>
            ) : null}
            {program.successCriterion ? (
              <p className="panel-prg-krit" data-testid="prg-kriterium-lest">
                <Ikon navn="flag-outline" /> Vi har rett hvis <b>{program.successCriterion.text}</b>
                {program.successCriterion.target ? (
                  <>
                    {' '}
                    er <b>{program.successCriterion.target}</b>
                  </>
                ) : null}
              </p>
            ) : null}
          </div>
          {/* HELTEN (DD-67): datoen som tall. På et avsluttet program er konklusjonen helten. */}
          <p className={`panel-prg-helt${erRapport ? ' panel-prg-helt-liten' : ''}`} data-testid="prg-helt">
            <span className="panel-etikett">{h.etikett}</span>
            <span className="panel-prg-helt-tall" data-testid="prg-helt-tall">
              {h.tall}
            </span>
            {/* «om 2 dager» er body, ikke small: det er datoens betydning, ikke en etikett
                (konseptets `.helt .l` er 16, `.helt .s` er 13). */}
            <span className="panel-prg-helt-under">{h.under}</span>
          </p>
        </div>

        <Sporingslinje ledd={sporLedd(program)} etikett="Hvor programmet står" />

        {program.cancelReason ? (
          <p className="panel-prg-grunnrad">
            <span className="panel-etikett">Begrunnelse</span> {program.cancelReason}
          </p>
        ) : null}

        {erRapport ? (
          <>
            <FrossenRapport rapport={rapport.data!} fraProgrammet={{ saker: saker.length, kjent: kjentFeilTreff }} />
            {/* H3.4: rapporten peker til hypotesen den kan bli et forslag til (skjerm 9, 17a, 17b). */}
            {program.fromHypothesis ? <HypoteseRad hyp={program.fromHypothesis} /> : null}
          </>
        ) : program.status === 'utkast' ? null : (
          <Tallrekke rader={tall} />
        )}

        {/* Funn-linja: én lenke til innboksen, og ett ledd til. På et avsluttet program står
            samme tall i rapportens rad «Fra programmet» — aldri to steder. */}
        {!erRapport && program.status !== 'utkast' ? (
          <div className="panel-prg-funn" data-testid="prg-funn">
            {funn.isPending ? (
              <span className="panel-etikett" role="status" aria-busy="true">
                Henter funn …
              </span>
            ) : saker.length === 0 ? (
              <span className="panel-etikett" role="status">
                Ingen saker fra programmet ennå.
              </span>
            ) : (
              <>
                <Link to="/innboks" className="panel-lenke" data-testid="prg-funn-lenke">
                  <Ikon navn="document-text-outline" /> {saker.length} saker fra programmet{smal ? '' : ' · Innboks'}
                </Link>
                {kjentFeilTreff > 0 ? <span className="panel-etikett">{kjentFeilTreff} traff en kjent feil</span> : null}
              </>
            )}
          </div>
        ) : null}

        {/* DD-12: Inviter finnes BARE i Åpen, og tokenene bare rett etter kallet. */}
        {program.status === 'aapen' && tokens ? (
          <section className="panel-prg-tokens" aria-labelledby="prg-tokens-h" data-testid="prg-tokens">
            <div className="panel-prg-seksjonshode">
              <b id="prg-tokens-h">{tokens.length} tokener — kopier og send nå</b>
              <span className="panel-etikett">vises én gang</span>
            </div>
            <table className="panel-prg-tabell">
              <thead>
                <tr>
                  <th scope="col">Pseudonym</th>
                  <th scope="col">Token</th>
                  <th scope="col">
                    <span className="panel-prg-skjult">Kopier</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((i) => (
                  <tr key={i.id} data-testid={`prg-token-rad-${i.subject}`}>
                    <td className="panel-prg-mono">{kortSubject(i.subject)}</td>
                    <td className="panel-prg-mono">
                      <code data-testid={`prg-token-${i.subject}`}>{i.token}</code>
                    </td>
                    <td>
                      <button type="button" className="panel-knapp-lenke" onClick={() => void navigator.clipboard?.writeText(i.token ?? '')} aria-label={`Kopier tokenet til ${kortSubject(i.subject)}`} data-testid={`prg-token-kopier-${i.subject}`}>
                        <Ikon navn="copy-outline" /> Kopier
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="panel-prg-handlingsrad">
              <Knapp label="Kopier alle" status onClick={() => void navigator.clipboard?.writeText(tokens.map((i) => `${i.subject}\t${i.token ?? ''}`).join('\n'))} testid="prg-tokens-kopier-alle" />
              <Knapp label="Ferdig — jeg har sendt dem" lenke onClick={() => setTokens(null)} testid="prg-tokens-ferdig" />
            </div>
          </section>
        ) : null}

        {program.status === 'aapen' && !tokens ? (
          <section className="panel-prg-inviter" aria-labelledby="prg-inviter-h">
            <div className="panel-prg-seksjonshode">
              <b id="prg-inviter-h">Inviter</b>
              <span className="panel-etikett">ett token per pseudonym · varer i 14 dager</span>
            </div>
            <label className="panel-felt">
              <span className="panel-prg-skjult">Pseudonymer</span>
              <textarea rows={3} value={pseudonymer} onChange={(e) => setPseudonymer(e.target.value)} data-testid="prg-subjects" placeholder="Pseudonymer, ett per linje" />
            </label>
            <label className="panel-prg-avkryssing">
              <input type="checkbox" checked={samtykke} onChange={(e) => setSamtykke(e.target.checked)} data-testid="prg-samtykke" />
              <span>Kanalen vi sender invitasjonen i har eget samtykke hos oss.</span>
            </label>
            <div className="panel-prg-handlingsrad">
              <Knapp label="Opprett invitasjoner" status onClick={send} grunn={inviterGrunn} testid="prg-inviter" />
            </div>
            <Om tittel="invitasjonene">
              <p>
                Plattformen sender aldri invitasjonen selv. Tokenet kommer i svaret her, og som hendelsen <code>program.invitation_created</code> til systemet deres — dere velger kanalen og bærer samtykket for den.
              </p>
              <p>Samtykket til selve programmet gir testeren ved aksept, per program, og det arves aldri fra invitasjonen.</p>
              <p>En invitasjon varer i 14 dager. Det finnes én per program og pseudonym: inviterer dere det samme pseudonymet på nytt, byttes tokenet, og det gamle slutter å virke.</p>
            </Om>
            {feil ? (
              <p role="alert" className="panel-melding panel-melding-feil">
                <Ikon navn="warning-outline" /> {feil}
              </p>
            ) : null}
          </section>
        ) : null}

        {program.status === 'aapen' && rader.length > 0 ? (
          <section className="panel-prg-seksjon" aria-labelledby="prg-invitasjoner-h">
            <div className="panel-prg-seksjonshode">
              <b id="prg-invitasjoner-h">Invitasjoner · {rader.length}</b>
            </div>
            <table className="panel-prg-tabell" data-testid="prg-invitasjoner">
              <thead>
                <tr>
                  <th scope="col">Pseudonym</th>
                  <th scope="col">Status</th>
                  <th scope="col">Svart</th>
                </tr>
              </thead>
              <tbody>
                {rader.map((i) => {
                  const s = INVITASJONSSTATUS[i.status] ?? { ikon: 'help-circle-outline', navn: i.status };
                  return (
                    <tr key={i.id} data-testid={`prg-invitasjon-${i.subject}`}>
                      <td className="panel-prg-mono">{kortSubject(i.subject)}</td>
                      <td>
                        <span className={`panel-prg-ord${s.tone ? ` ${s.tone}` : ''}`}>
                          <Ikon navn={s.ikon} /> {s.navn}
                        </span>
                      </td>
                      <td className="panel-prg-dempet">{i.answeredAt ? kortDatoCelle(i.answeredAt) : `utløper ${kortDatoCelle(i.expiresAt)}`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        ) : null}

        {/* Deltakerne (DD-38): tabell, ikke liste. Kolonnene sorterer ikke, det finnes ingen
            sum, ingen «mest aktive» og ingen poeng — tabellform er ikke rangering. */}
        {alle.length > 0 ? (
          <section className="panel-prg-seksjon" aria-labelledby="prg-deltakere-h">
            <div className="panel-prg-seksjonshode">
              <b id="prg-deltakere-h">Deltakere · {alle.length}</b>
            </div>
            <table className="panel-prg-tabell" data-testid="prg-deltakere">
              <thead>
                <tr>
                  <th scope="col">Pseudonym</th>
                  <th scope="col">Status</th>
                  <th scope="col">{smal ? 'Oppg.' : 'Oppgaver'}</th>
                  {smal ? null : <th scope="col">Kontakter</th>}
                </tr>
              </thead>
              <tbody>
                {viste.map((d) => {
                  const o = deltakerord(d);
                  return (
                    <tr key={d.id} data-testid={`prg-deltaker-${d.subject}`}>
                      <td className="panel-prg-mono">{kortSubject(d.subject)}</td>
                      <td>
                        <span className={`panel-prg-ord${o.tone ? ` ${o.tone}` : ''}`}>
                          <Ikon navn={o.ikon} /> {o.navn}
                        </span>
                      </td>
                      <td className="panel-prg-dempet">
                        {d.tasksDone} av {d.tasksTotal}
                      </td>
                      {smal ? null : (
                        <td className="panel-prg-dempet">
                          {d.contactsSent} av {program.maxContacts}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {alle.length > viste.length ? (
              <button type="button" className="panel-knapp-lenke" onClick={() => setAlleDeltakere(true)} data-testid="prg-vis-alle-deltakere">
                Vis alle {alle.length} <Ikon navn="chevron-forward-outline" />
              </button>
            ) : null}
          </section>
        ) : deltakere.isPending ? (
          <div role="status" aria-busy="true" aria-label="Laster deltakere" className="panel-skjelett">
            <span className="panel-skjelett-linje" />
          </div>
        ) : null}
      </div>

      {/* FOTEN (DD-58, presisert P4): ett primærvalg, og det følger status. Åpen har ingen —
          «Opprett invitasjoner» står i kroppen, fylt i statusens flate. Avbrutt har ingen fot. */}
      {program.status === 'avbrutt' ? null : (
        <footer className="panel-prg-fot" data-testid="prg-fot">
          {primærValg ? <Knapp label="Avslutt med rapport" primaer onClick={() => setAvslutter(true)} grunn={kanSkrive ? null : 'Rollen leser kan ikke avslutte programmer'} testid="prg-avslutt" /> : null}
          <Knapp label="Rediger" onClick={onRediger} grunn={skriveGrunn} testid="prg-rediger" />
          {kanAvbryte && !smal ? <Knapp label="Avbryt programmet" fare onClick={() => setAvbryter(true)} grunn={skriveGrunn} testid="prg-avbryt" /> : null}
        </footer>
      )}

      {smal && mer && kanAvbryte ? (
        <div className="panel-prg-mermeny" role="group" aria-label="Mer">
          <Knapp label="Avbryt programmet" fare onClick={() => setAvbryter(true)} grunn={skriveGrunn} testid="prg-avbryt" />
        </div>
      ) : null}

      {avslutter ? (
        <Avslutt
          program={program}
          svar={rapport.data?.payload.responses ?? 0}
          opptatt={avslutt.isPending}
          onAvbryt={() => setAvslutter(false)}
          onAvslutt={(body: ProgramReportCreate) => {
            setFeil(null);
            avslutt
              .mutateAsync(body)
              .then(() => setAvslutter(false))
              .catch((e: unknown) => setFeil(erApiFeil(e) ? e.brukertekst : 'Kunne ikke avslutte programmet. Prøv igjen.'));
          }}
        />
      ) : null}

      {avbryter ? (
        <Dialog tittelId="prg-avbryt-h" onLukk={() => setAvbryter(false)} testid="prg-avbryt-dialog">
          <h3 id="prg-avbryt-h" className="panel-h3">
            Avbryt «{program.name}»?
          </h3>
          <p className="panel-tekst">Programmet lukkes for alle. Begrunnelsen sendes til systemet deres, så testerne får vite hvorfor — ikke bare at det ble stille.</p>
          <label className="panel-felt">
            <span className="panel-felt-navn">Begrunnelse</span>
            <textarea rows={2} maxLength={280} value={grunn} onChange={(e) => setGrunn(e.target.value)} data-testid="prg-avbryt-grunn" placeholder="Funksjonen ble lagt på is." />
          </label>
          <div className="panel-prg-knapper">
            <Knapp label="Behold programmet" kbd="Esc" onClick={() => setAvbryter(false)} testid="prg-avbryt-nei" />
            <Knapp
              label="Avbryt programmet"
              fare
              onClick={() => {
                onAvbryt(grunn.trim());
                setAvbryter(false);
              }}
              grunn={grunn.trim() === '' ? 'Begrunnelsen er obligatorisk — den går til testerne' : opptatt ? 'Lagrer …' : null}
              testid="prg-avbryt-ja"
            />
          </div>
        </Dialog>
      ) : null}
    </section>
  );
}

/** «1. nov» i en tabellcelle — dag og kort måned, uten punktumet nb-NO setter på måneden. */
function kortDatoCelle(iso: string): string {
  return new Date(iso).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' }).replace(/\.$/, '');
}

